import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import {
  DownloadOutlined,
  EyeOutlined,
  UndoOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  TruckOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SkeletonOrderCard from "../components/SkeletonOrderCard";

const Container = styled.div`
  /* min-height: 100vh; */
  background: #fff;
`;

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #000;
  margin: 0 0 30px 0;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const FilterButton = styled.button`
  padding: 10px 16px;
  background: ${(props) => (props.active ? "#000" : "#f9f9f9")};
  color: ${(props) => (props.active ? "#fff" : "#000")};
  border: 1px solid ${(props) => (props.active ? "#000" : "#ddd")};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: capitalize;

  &:hover {
    border-color: #000;
    background: ${(props) => (props.active ? "#000" : "#f0f0f0")};
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
  }
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const OrderCard = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;
  cursor: pointer;
  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border-color: #ddd;
  }
`;

const OrderHeader = styled.div`
  background: #f9f9f9;
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h4 {
    font-size: 12px;
    font-weight: 600;
    color: #999;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  p {
    font-size: 14px;
    font-weight: 600;
    color: #000;
    margin: 0;
  }

  span {
    font-size: 13px;
    color: #666;
    margin: 0;
  }
`;

const OrderBody = styled.div`
  padding: 20px;
`;

const ItemsSection = styled.div`
  margin-bottom: 24px;
`;

const ItemsTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #000;
  margin: 0 0 16px 0;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OrderItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px;

  border-radius: 6px;

  @media (max-width: 768px) {
    gap: 10px;
    padding: 10px;
  }
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 4px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;

  h4 {
    font-size: 13px;
    font-weight: 600;
    color: #000;
    margin: 0;
  }

  p {
    font-size: 12px;
    color: #666;
    margin: 0;
  }
`;

const OrderFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid #eee;
  background: #fff;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

const OrderTotal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h4 {
    font-size: 12px;
    font-weight: 600;
    color: #999;
    margin: 0;
    text-transform: uppercase;
  }

  p {
    font-size: 20px;
    font-weight: 700;
    color: #000;
    margin: 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;

    button {
      flex: 1;
    }
  }
`;

const ActionButton = styled.button`
  padding: 10px 16px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  &:hover {
    background: #333;
  }

  &.secondary {
    background: #f0f0f0;
    color: #000;
    border: 1px solid #ddd;

    &:hover {
      background: #e0e0e0;
    }
  }

  svg {
    font-size: 14px;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${(props) => {
    switch (props.status) {
      case "delivered":
        return "#e8f5e9";
      case "processing":
        return "#fff3e0";
      case "placed":
        return "#e3f2fd";
      case "cancelled":
        return "#ffebee";
      case "payment_pending":
        return "#fff8e1";
      default:
        return "#f5f5f5";
    }
  }};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => {
    switch (props.status) {
      case "delivered":
        return "#2e7d32";
      case "processing":
        return "#f57c00";
      case "placed":
        return "#1565c0";
      case "cancelled":
        return "#c62828";
      case "payment_pending":
        return "#f57f17";
      default:
        return "#666";
    }
  }};

  svg {
    font-size: 13px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;

  .icon {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #666;
    margin: 0 0 8px 0;
  }

  p {
    font-size: 14px;
    color: #999;
    margin: 0;
  }
`;

const getStatusIcon = (status) => {
  switch (status) {
    case "delivered":
      return <CheckCircleOutlined />;
    case "processing":
      return <ClockCircleOutlined />;
    case "placed":
      return <TruckOutlined />;
    case "cancelled":
      return <UndoOutlined />;
    case "payment_pending":
      return <ClockCircleOutlined />;
    default:
      return null;
  }
};

const Orders = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);

  const filters = [
    "All",
    "delivered",
    "placed",
    "processing",
    "cancelled",
    "payment_pending",
  ];

  const getStatusLabel = (status) => {
    const statusMap = {
      delivered: "Delivered",
      placed: "Placed",
      processing: "Processing",
      cancelled: "Cancelled",
      payment_pending: "Payment Pending",
    };
    return statusMap[status] || status;
  };

  const filteredOrders =
    selectedFilter === "All"
      ? orders
      : orders.filter((order) => order.status === selectedFilter);

  // Fetch orders from API
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${apiBase}/orders`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data.status && data.data?.data) {
          // Transform API response to match component format
          const transformedOrders = data.data.data.map((order) => ({
            id: order.order_number,
            date: new Date(order.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            status:
              order.payment_status !== "paid" && order.order_status === "placed"
                ? "payment_pending"
                : order.order_status,
            totalAmount: parseFloat(order.total),
            items: order.items.map((item) => ({
              id: item.id,
              name: item.product?.name || "Product",
              quantity: item.quantity,
              price: parseFloat(item.price),
              image: item.product?.image?.url || "",
              size: item.variant?.size?.name,
              color: item.variant?.color?.name,
            })),
            orderId: order.id,
            paymentStatus: order.payment_status,
          }));
          setOrders(transformedOrders);
          setError(null);
        } else {
          setError(data.message || "Failed to fetch orders");
          setOrders([]);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Error fetching orders. Please try again.");
        setOrders([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchOrders();
    }

    window.scrollTo(0, 0);
  }, [token]);

  return (
    <Container>
      {/* <Navbar /> */}

      <Wrapper>
        <PageTitle>My Orders</PageTitle>

        {/* Filter Bar */}
        <FilterBar>
          {filters.map((filter) => (
            <FilterButton
              key={filter}
              active={selectedFilter === filter}
              onClick={() => setSelectedFilter(filter)}
            >
              {filter === "All" ? "All" : getStatusLabel(filter)}
            </FilterButton>
          ))}
        </FilterBar>

        {/* Orders List */}
        {isLoading ? (
          <SkeletonOrderCard count={3} />
        ) : filteredOrders.length > 0 ? (
          <OrdersGrid>
            {filteredOrders.map((order) => {
              return (
                <OrderCard
                  key={order.id}
                  onClick={() =>
                    navigate(`/order/${order.id}/${order.orderId}`)
                  }
                >
                  <OrderHeader>
                    <HeaderInfo>
                      <h4>Order ID</h4>
                      <p>{order.id}</p>
                    </HeaderInfo>

                    <HeaderInfo>
                      <h4>Order Date</h4>
                      <p>{order.date}</p>
                    </HeaderInfo>

                    <HeaderInfo>
                      <h4>Status</h4>
                      <StatusBadge status={order.status}>
                        {getStatusIcon(order.status)}
                        {getStatusLabel(order.status)}
                      </StatusBadge>
                    </HeaderInfo>

                    <HeaderInfo>
                      <h4>Total Amount</h4>
                      <p>₹{order.totalAmount.toLocaleString("en-IN")}</p>
                    </HeaderInfo>
                  </OrderHeader>

                  <OrderBody>
                    {/* Items Section */}
                    <ItemsSection>
                      <ItemsList>
                        {order.items.map((item) => (
                          <OrderItem key={item.id}>
                            <ItemImage src={item.image} alt={item.name} />
                            <ItemInfo>
                              <h4>{item.name}</h4>
                              <p>
                                ₹
                                {(item.price * item.quantity).toLocaleString(
                                  "en-IN",
                                )}
                              </p>
                            </ItemInfo>
                          </OrderItem>
                        ))}
                      </ItemsList>
                    </ItemsSection>
                  </OrderBody>

                  {/* Footer with Total and Actions */}
                  <OrderFooter>
                    <OrderTotal>
                      <h4>Total</h4>
                      <p>₹{order.totalAmount.toLocaleString("en-IN")}</p>
                    </OrderTotal>

                    {/* <ActionButtons>
                      <ActionButton
                        title="View Order Details"
                        onClick={() => navigate(`/order/${order.orderId}`)}
                      >
                        <EyeOutlined />
                        View Details
                      </ActionButton>
                    </ActionButtons> */}
                  </OrderFooter>
                </OrderCard>
              );
            })}
          </OrdersGrid>
        ) : (
          <EmptyState>
            <div className="icon">
              <ShoppingOutlined />
            </div>
            <h3>No Orders Found</h3>
            <p>
              You don't have any orders with status "
              {selectedFilter === "All"
                ? "All"
                : getStatusLabel(selectedFilter)}
              "
            </p>
          </EmptyState>
        )}
      </Wrapper>

      <Footer />
    </Container>
  );
};

export default Orders;
